<template>
  <div class="wrraper">
    <Breadcrumb
      :items="items"
      search_route="/product-promotions/add"
      :search_title="$t('breadcrumb.productPromotions.add')"
      icon="fa-plus"
    />

    <section class="headSection main-filter">
      <form class="w-100" @submit.prevent="getData(1)">
        <div class="frm-controls w-100 p-0 row align-items-end">
          <div class="col-lg-4 col-md-6 promo-search">
            <div class="input_wrapper top_label">
              <label class="form-label">{{ $t("forms.labels.product") }}</label>
              <input
                v-model="productQuery"
                type="text"
                class="form-control"
                placeholder="Search by name, code, or ID"
                autocomplete="off"
                @focus="productMenu = true"
                @input="onProductQuery"
                @blur="closeProductMenu"
              />
              <ul v-show="productMenu" class="product-menu">
                <li v-if="needsMoreSearch" class="product-menu__hint">
                  Type a name, code, or ID.
                </li>
                <li v-else-if="searching" class="product-menu__hint">{{ $t("table.loadingData") }}</li>
                <li v-else-if="!products.length" class="product-menu__hint">No products match that search.</li>
                <template v-else>
                  <li
                    v-for="product in products"
                    :key="product.id"
                    class="product-menu__item"
                    @mousedown.prevent="pickProduct(product)"
                  >
                    <strong>{{ product.name || product.id }}</strong>
                    <span>{{ productMeta(product) }}</span>
                  </li>
                </template>
              </ul>
            </div>
          </div>

          <div class="col-lg-2 col-md-6">
            <div class="input_wrapper top_label">
              <label class="form-label">Promotion type</label>
              <select v-model="filters.offer_type" class="form-control" @change="getData(1)">
                <option value="">All types</option>
                <option value="same_item_gift">Buy 1 Get 1 Free</option>
                <option value="same_item_discount">Buy 1 Get Discount on Second</option>
              </select>
            </div>
          </div>

          <div class="col-lg-2 col-md-4">
            <div class="input_wrapper top_label">
              <label class="form-label">Status</label>
              <select v-model="filters.status" class="form-control" @change="getData(1)">
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="scheduled">Scheduled</option>
                <option value="expired">Expired</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div class="col-lg-2 col-md-4">
            <div class="input_wrapper top_label">
              <label class="form-label">From</label>
              <input v-model="filters.from" type="datetime-local" class="form-control" @change="getData(1)" />
            </div>
          </div>

          <div class="col-lg-2 col-md-4">
            <div class="input_wrapper top_label">
              <label class="form-label">To</label>
              <input v-model="filters.to" type="datetime-local" class="form-control" @change="getData(1)" />
            </div>
          </div>

          <div class="col-12 filter-actions">
            <button type="submit" class="button_style_1">{{ $t("table.search") }}</button>
            <button type="button" class="button_style_2" @click="clearFilters">Clear</button>
          </div>
        </div>
      </form>
    </section>

    <main>
      <v-data-table
        class="elevation-1 thumb diff_table"
        :headers="headers"
        :items="rows"
        :loading="loading"
        :loading-text="$t('table.loadingData')"
        item-key="id"
        hide-default-footer
      >
        <template v-slot:no-data>
          {{ loading ? $t("table.loadingData") : $t("table.noData") }}
        </template>

        <template v-slot:top>
          <h3 class="title table-title">{{ $t("titles.productPromotions") }}</h3>
          <v-dialog v-model="dialogDelete" max-width="500px">
            <v-card>
              <v-card-title class="text-h5 justify-center">
                {{ $t("table.deletedialog.areYouSure") }}
              </v-card-title>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="#1B5E20" @click="deleteItemConfirm">
                  {{ $t("table.deletedialog.ok") }}
                </v-btn>
                <v-btn color="#F44336" @click="dialogDelete = false">
                  {{ $t("table.deletedialog.cancel") }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>

        <template v-slot:[`item.product_name`]="{ item }">
          <div class="product-cell">
            <strong>{{ item.product_name || "—" }}</strong>
            <span>#{{ item.product_id }}</span>
          </div>
        </template>

        <template v-slot:[`item.start_at`]="{ item }">
          <div class="when-cell">
            <strong>{{ whenParts(item.start_at).date }}</strong>
            <span>{{ whenParts(item.start_at).time }}</span>
          </div>
        </template>

        <template v-slot:[`item.end_at`]="{ item }">
          <div class="when-cell" v-if="item.end_at">
            <strong>{{ whenParts(item.end_at).date }}</strong>
            <span>{{ whenParts(item.end_at).time }}</span>
          </div>
          <span v-else>—</span>
        </template>

        <template v-slot:[`item.offer_type`]="{ item }">
          <span class="type-pill" :class="item.offer_type">
            {{ typeLabel(item.offer_type) }}
          </span>
        </template>

        <template v-slot:[`item.discount_value`]="{ item }">
          {{ item.discount_value === null || item.discount_value === undefined ? "—" : item.discount_value + "%" }}
        </template>

        <template v-slot:[`item.max_quantity`]="{ item }">
          {{ item.max_quantity ? item.max_quantity : "—" }}
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <span class="status" :class="statusClass(item.status)">
            {{ statusLabel(item.status) }}
          </span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="_actions">
            <v-icon class="edit" small @click="editItem(item)">fal fa-edit</v-icon>
            <button type="button" class="toggle-btn" @click="toggleActive(item)">
              {{ item.is_active ? "Deactivate" : "Activate" }}
            </button>
            <v-icon class="delete" small @click="askDelete(item)">fal fa-trash</v-icon>
          </div>
        </template>
      </v-data-table>
    </main>

    <div class="pagination_container text-center mb-5 d-flex justify-content-end">
      <v-pagination
        v-model.number="pagination.current_page"
        :length="pagination.last_page || 1"
        :total-visible="5"
        @input="getData"
      ></v-pagination>
    </div>
  </div>
</template>

<script>
export default {
  name: "AllProductPromotions",
  data() {
    return {
      loading: false,
      searching: false,
      searchTimer: null,
      dialogDelete: false,
      pendingDelete: null,
      rows: [],
      products: [],
      selectedProduct: null,
      productQuery: "",
      productMenu: false,
      filters: { product_id: "", offer_type: "", status: "", from: "", to: "" },
      pagination: { current_page: 1, last_page: 1 },
      items: [
        {
          text: this.$t("breadcrumb.mainPage"),
          disabled: false,
          href: "/",
        },
        {
          text: this.$t("breadcrumb.productPromotions.title"),
          disabled: false,
          href: "/product-promotions/show-all",
        },
        {
          text: this.$t("breadcrumb.productPromotions.all"),
          disabled: true,
          href: "",
        },
      ],
      headers: [
        { text: "Product", value: "product_name", width: "26%" },
        { text: "Promotion Type", value: "offer_type" },
        { text: "Discount %", value: "discount_value", width: "110px" },
        { text: "Max / order", value: "max_quantity", width: "110px" },
        { text: "Start", value: "start_at", width: "130px" },
        { text: "End", value: "end_at", width: "130px" },
        { text: "Status", value: "status", width: "120px" },
        { text: "Actions", value: "actions", sortable: false, align: "end", width: "180px" },
      ],
    };
  },
  computed: {
    needsMoreSearch() {
      const keyword = (this.productQuery || "").trim();
      return keyword.length < 2 && !/^\d+$/.test(keyword);
    },
  },
  methods: {
    productLabel(product) {
      if (!product) return "";
      return `${product.name || product.id} ${this.productMeta(product)}`;
    },
    productMeta(product) {
      if (!product) return "";
      return product.code ? `#${product.id} · ${product.code}` : `#${product.id}`;
    },
    whenParts(value) {
      if (!value) return { date: "—", time: "" };
      const [date, time] = String(value).split(" ");
      return { date: date || "—", time: time ? time.slice(0, 5) : "" };
    },
    typeLabel(type) {
      return type === "same_item_gift" ? "Buy 1 Get 1 Free" : "Buy 1 Get Discount on Second";
    },
    statusLabel(status) {
      const labels = {
        active: "Active",
        scheduled: "Scheduled",
        expired: "Expired",
        inactive: "Inactive",
      };
      return labels[status] || status || "—";
    },
    statusClass(status) {
      if (status === "active") return "paid";
      if (status === "inactive") return "unpaid";
      if (status === "expired") return "canceled";
      return "scheduled";
    },
    onProductQuery() {
      this.productMenu = true;
      if (this.selectedProduct && this.productQuery !== this.productLabel(this.selectedProduct)) {
        this.selectedProduct = null;
        if (this.filters.product_id) {
          this.filters.product_id = "";
          this.getData(1);
        }
      }
      this.onSearch(this.productQuery);
    },
    pickProduct(product) {
      this.selectedProduct = product;
      this.productQuery = this.productLabel(product);
      this.productMenu = false;
      this.onProductFilter(product);
    },
    closeProductMenu() {
      setTimeout(() => {
        this.productMenu = false;
      }, 150);
    },
    onSearch(query) {
      clearTimeout(this.searchTimer);
      const keyword = (query || "").trim();
      if (keyword.length < 2 && !/^\d+$/.test(keyword)) {
        this.searching = false;
        this.products = this.selectedProduct ? [this.selectedProduct] : [];
        return;
      }
      this.searchTimer = setTimeout(() => this.searchProducts(keyword), 300);
    },
    searchProducts(keyword = "") {
      this.searching = true;
      this.$axios
        .get("products", { params: { keyword, per_page: 20 } })
        .then((res) => {
          const payload = res.data.data;
          const rows = payload.data || payload || [];
          this.products = Array.isArray(rows) ? rows : [];
          if (this.selectedProduct && !this.products.some((row) => row.id === this.selectedProduct.id)) {
            this.products = [this.selectedProduct, ...this.products];
          }
        })
        .catch((err) => this.notifyError(err))
        .finally(() => {
          this.searching = false;
        });
    },
    onProductFilter(product) {
      const nextId = product ? product.id : "";
      if (String(nextId) === String(this.filters.product_id || "")) return;
      this.filters.product_id = nextId;
      this.getData(1);
    },
    clearFilters() {
      this.filters = { product_id: "", offer_type: "", status: "", from: "", to: "" };
      this.selectedProduct = null;
      this.productQuery = "";
      this.productMenu = false;
      this.products = [];
      this.getData(1);
    },
    getData(page = 1) {
      this.loading = true;
      const params = { page, per_page: 15 };
      Object.keys(this.filters).forEach((key) => {
        if (this.filters[key]) {
          params[key] = key === "from" || key === "to" ? this.filters[key].replace("T", " ") : this.filters[key];
        }
      });
      this.$axios
        .get("product-promotions", { params })
        .then((res) => {
          this.rows = res.data.data || [];
          this.pagination = res.data.meta || { current_page: 1, last_page: 1 };
        })
        .catch((err) => this.notifyError(err))
        .finally(() => {
          this.loading = false;
        });
    },
    editItem(item) {
      this.$router.push(`/product-promotions/edit/${item.id}`);
    },
    toggleActive(item) {
      this.$axios
        .put(`product-promotions/${item.id}`, {
          base_product_id: item.product_id,
          offer_type: item.offer_type,
          discount_value: item.offer_type === "same_item_discount" ? item.discount_value : undefined,
          start_at: item.start_at,
          end_at: item.end_at,
          is_active: !item.is_active,
        })
        .then((res) => {
          this.notifySuccess(res);
          this.getData(this.pagination.current_page);
        })
        .catch((err) => this.notifyError(err));
    },
    askDelete(item) {
      this.pendingDelete = item;
      this.dialogDelete = true;
    },
    deleteItemConfirm() {
      if (!this.pendingDelete) return;
      this.$axios
        .delete(`product-promotions/${this.pendingDelete.id}`)
        .then((res) => {
          this.dialogDelete = false;
          this.pendingDelete = null;
          this.notifySuccess(res);
          this.getData(1);
        })
        .catch((err) => this.notifyError(err));
    },
    notifySuccess(res) {
      this.$iziToast.success({
        timeout: 2000,
        message: res?.data?.message || this.$t("save"),
        position: "bottomRight",
      });
    },
    notifyError(err) {
      this.$iziToast.error({
        timeout: 3000,
        message: err?.response?.data?.message || this.$t("table.noData"),
        position: "bottomRight",
      });
    },
  },
  created() {
    this.getData();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
};
</script>

<style lang="scss" scoped>
.filter-actions {
  display: flex;
  gap: 12px;
  margin: 4px 0 8px;

  button {
    width: auto;
    min-width: 140px;
  }
}

.promo-search {
  position: relative;
  z-index: 8;

  .form-control {
    width: 100%;
  }
}

.product-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 30;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  max-height: 280px;
  overflow: auto;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
}

.product-menu__hint,
.product-menu__item {
  padding: 8px 14px;
}

.product-menu__hint {
  color: #6b747c;
  font-size: 13px;
}

.product-menu__item {
  display: flex;
  flex-direction: column;
  line-height: 1.35;
  cursor: pointer;

  span {
    font-size: 12px;
    opacity: 0.75;
  }

  &:hover {
    background: #eef2ff;
  }
}

.product-cell,
.when-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;

  span {
    font-size: 12px;
    opacity: 0.7;
  }
}

.type-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: #eef2ff;
  color: #273c98;

  &.same_item_discount {
    background: #fff4e8;
    color: #c2410c;
  }
}

.status.scheduled {
  background: #fff4e8;
  color: #c2410c;
}

.toggle-btn {
  border: 0;
  background: transparent;
  color: var(--mainColor-2, #273c98);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 4px;
}

._actions {
  display: flex;
  align-items: center;
}
</style>
